// §I8 — cross-org notification badge counts.
//
// Hits the server's `/auth/me/org-notifications` endpoint, which forwards
// via HMAC to the governance `symbols-admin-ops` edge fn's
// `notifications.count_by_org` handler. Response shape:
//   { data: { counts: { [orgId]: { mentions, ticketsAssigned, meetingInvites,
//                                  mailUnread } } } }
//
// `AppWorkspaceSwitcher`'s per-org `NotifBadge` reads `root.orgNotifCount[orgId]`
// as a single aggregate number — we sum the signals into one badge
// count per org. The signals still live behind the flat key so a future
// per-type surfacing (e.g. mention-only pulse, ticket-only indicator)
// can read `root.orgNotifSignals[orgId]` without refetching.
//
// THE SIGNAL LIST IS DATA, NOT A HAND-WRITTEN SUM. It used to be three
// destructured names added together, and a fourth type the server started
// sending was not "not yet wired" — it was silently dropped from BOTH the
// badge count and the per-type map this file promises a future reader
// (MAIL-BADGE-NOTIFICATIONS-WIDGET-1, `mailUnread`, architecture/MAIL.md
// §3.6). One list now drives the sum and the map together, so adding a type
// is one entry, and a type the server has not shipped yet reads 0 rather than
// turning the sum into NaN and printing "NaN" on the badge plate.
//
// `mailUnread` is unread mail the viewer is GRANTED — shared-inbox mail, a
// thread assigned to them, a failed send, and personal mail only on an account
// that opted in (§3.6). The server owns that filtering; this side owns only
// "do not drop what arrives".
//
// Fails-soft: any error leaves existing counts untouched. Server itself
// already fails-soft to `{counts:{}}` when governance URL is unset, so
// a 200 response with empty counts is normal during pre-wiring.

// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)

// The per-type signals the badge sums, in the order the server documents them.
// Exported so the suite pins the LIST rather than re-typing it — a name added
// here is a name the badge counts, with no other edit.
export const ORG_NOTIF_SIGNALS = ['mentions', 'ticketsAssigned', 'meetingInvites', 'mailUnread']

export const fetchOrgNotifCount = async (root) => {
  if (!root) return
  // Skip the poll when the tab is not visible — saves free-tier edge fn
  // invocations for users with the intranet backgrounded. The first call
  // on sign-in always goes through (document.hidden is false on active tab).
  if (typeof document !== 'undefined' && document.hidden) return
  if (!shellGetSDK().getAuthToken()) return
  try {
    // SDK method threads the auth header + base URL through TokenManager
    // and returns the unwrapped data payload directly.
    const data = await shellGetSDK().getMyOrgNotifications()
    const counts = (data && data.counts) || {}
    const flat = {}
    const signals = {}
    for (const [orgId, perType] of Object.entries(counts)) {
      const per = {}
      let total = 0
      for (const kind of ORG_NOTIF_SIGNALS) {
        const n = Number(perType?.[kind] || 0)
        per[kind] = n
        total += n
      }
      flat[orgId] = total
      signals[orgId] = per
    }
    root.update({ orgNotifCount: flat, orgNotifSignals: signals })
  } catch {
    // fail-soft — leave prior counts intact
  }
}
